import VueI18n, { LocaleMessageObject } from 'vue-i18n';
import Vue from 'vue';
import en from '@/locales/en.json';
import { formatString } from '@/common/bot/lib';
import { SupportedLanguages, LANGUAGES } from '@/locales/languages';
import { waitFor } from '@/common/utils';

/**
 * Custom translation function that can be access via `this.t` in Vue components. This is an extension to the underlying
 * `VueI18n` library. This can be used with new keys that are not yet defined in locales with default values.
 *
 * The return type is casted to string to help inside components. This means that the key should always resolve to a string.
 *
 * @param this Vue instance
 * @param key The key to echo
 * @param optsOrDefault The default string to echo or the formatting arguments.
 * @param def The default value to echo.
 * @returns string
 */
function translator (this: Vue, key: string, optsOrDefault?: (string[]|Record<string, string|boolean|number|null>) | string, def?: string) {
  if (this.$te(key) || (this.$i18n.fallbackLocale && hasFallback(this, key))) {
    const args: [string] | ([string, VueI18n.Values]) = optsOrDefault && typeof optsOrDefault === 'object' ? [key, optsOrDefault] : [key];
    return this.$t.apply(this, args as any) as string;
  }
  const str = (optsOrDefault && typeof optsOrDefault === 'string' && optsOrDefault) || def || '';
  const options = (optsOrDefault && typeof optsOrDefault === 'object' && optsOrDefault) || {};
  return formatString(str, options);
}

function hasFallback (instance: Vue, key: string) {
  const fallbackLocale = instance.$i18n.fallbackLocale;
  if (!fallbackLocale) return false;
  if (typeof fallbackLocale === 'string') { return instance.$te(key, fallbackLocale); }
  if (Array.isArray(fallbackLocale)) { return !!fallbackLocale.find(locale => instance.$te(key, locale)); }
  if (!Object.keys(fallbackLocale).length) { return false; }
  const locale = instance.$i18n.locale;
  const langList = (fallbackLocale[locale] || []).concat(fallbackLocale.default || []);
  return !!langList.find(locale => instance.$te(key, locale));
};

Vue.use(VueI18n);

// This applies a helper function
Vue.use(Vue => { Vue.prototype.t = translator; });

export interface LanguageChangeEvent {
  current: SupportedLanguages,
  previous: SupportedLanguages,
}
export type LanguageChangeListener = ($event: LanguageChangeEvent) => unknown;

export const i18n = new VueI18n({
  locale: 'en',
  fallbackLocale: 'en',
  messages: { en },
});

const LANG_EVENT = 'pl:lang:changed';
const LANG_EVENT_BEFORE_CHANGE = 'pl:lang:before:change';

/**
 * Set of languages that are available.
 */
const available = new Set<SupportedLanguages>(['en']);
/**
 * An event bus used for triggering changes.
 */
const bus = new Vue();

/**
 * Helper utility that fire event when language is changed.
 * @param locale The language to change into.
 * @returns The language changed.
 */
async function change (locale: SupportedLanguages) {
  const previous = i18n.locale;

  previous !== locale && bus.$emit(LANG_EVENT_BEFORE_CHANGE, { previous, current: locale } as LanguageChangeEvent);

  // Make sure to execute the "before" listeners
  await waitFor(0);

  i18n.locale = locale;
  // Fire event only on change.
  previous !== locale && bus.$emit(LANG_EVENT, { previous, current: locale } as LanguageChangeEvent);
  return locale;
}

/**
 * Register an event handler whenever language changes. If you have actions depending upon
 * language change, you can use the `before` param to execute them, before the language change
 * is applied.
 *
 * NOTE: Does not work for the first language change.
 * @param callback The event handler
 * @param before Executes just before setting the locale.
 * @returns A function that removes the listener.
 */
export function addListener (callback: LanguageChangeListener, before = false) {
  bus.$on(before ? LANG_EVENT_BEFORE_CHANGE : LANG_EVENT, callback);
  return () => removeListener(callback, before);
}

/**
 * Removes the given event listener.
 * @param callback Event handler
 */
export function removeListener (callback: LanguageChangeListener, before = false) {
  bus.$off(before ? LANG_EVENT_BEFORE_CHANGE : LANG_EVENT, callback);
}

/**
 * Change the language of the application. The languages will be loaded asynchronously.
 *
 * @param language The language to change.
 * @returns A promise resolving the changed language.
 */
export async function setLanguage (toChange: SupportedLanguages | 'pt-br') {
  // TODO: Fix language code.
  const language: SupportedLanguages = toChange === 'pt-br' ? 'pt' : toChange;
  if (i18n.locale === language || available.has(language)) {
    return change(language);
  }

  const locales: LocaleMessageObject = await import(/* webpackChunkName: "lang-[request]" */`@/locales/${language}.json`)
    .then(module => module.default);

  i18n.setLocaleMessage(language, locales);
  available.add(language);
  return change(language);
}

export function getCurrentLanguageName () {
  return getName(i18n.locale as SupportedLanguages);
}

export function getName (lang: SupportedLanguages) {
  return LANGUAGES[lang]?.name || '';
}

/**
 * Test if the given locale code is supported by the application.
 */
export function isSupported (toChange: string): toChange is SupportedLanguages | 'pt-br' {
  // TODO: Fix language code.
  const language = (toChange === 'pt-br' ? 'pt' : toChange) as SupportedLanguages;
  return Boolean(LANGUAGES[language]);
}

declare module 'vue/types/vue' {
  interface Vue {
    t: typeof translator
  }
}
